// AWS UI Components
import { Box, Button, Container, Header, Multiselect, MultiselectProps, Select, SelectProps, SpaceBetween } from '@amzn/awsui-components-core';
import FormField from '@amzn/awsui-components-core/form-field';

// React and utilities
import React, { useEffect, useState, useMemo, useRef } from 'react';
import moment from 'moment';
import { logger } from 'src/logger';
import { areFiltersEqual, getMultiSelectPlaceHolderValue } from 'src/utilities/CommonUtilities';
import { sortedDateArrayMMMYYYYFormat, getCurrentTime, getTimeDifference } from 'src/utilities/DateTimeUtilities';

// Context and constants
import { useSharedFilters } from '../shared/context/SharedFiltersProvider';
import { useTickets } from '../shared/context/TicketsProvider';
import { useUserMetrics } from '../shared/context/UserMetricsProvider';
import { filtersLabel } from '../FinTechOpsCommonElements';
import { ALL_APPLICATIONS, FilterDateFormat_MMM_YYYY } from '../FinTechOpsConstants';
import { DURATION_OPTIONS } from './BusinessReportingConstants';

// Types and interfaces
import { TicketsParsedDataItem } from '../tickets/TicketsInterfaces';
import { UserMetricsParsedData } from '../user-metrics/UserMetricsInterfaces';
import {
  optionsObjectToStringArray,
  parseObjectToMultiSelectOptions_Sorted,
  OptionItem,
  returnFilterAgeSorted
} from '../FinTechOpsDataProcessingFunctions';

// Business reporting utilities
import {
  getBusinessReportingApplications,
  getDefaultSelections,
  getMonthlyOptions,
  getQuarterlyOptions,
  getMostRecentPeriod,
  validatePeriodSelection,
  convertQuarterToMonths
} from './BusinessReportingUtils';
import { processBusinessReportingTicketData, ProcessedBusinessReportingData } from './BusinessReportingTicketDataProcessing';
import { processBusinessReportingUserData, ProcessedBusinessReportingUserData } from './BusinessReportingUserDataProcessing';

import styles from '../fintech-ops-dashboard/styles.module.scss';
import { Titles } from 'src/components/das-finsuite/das-finsuite-constants';

// Interfaces
export interface BusinessReportingFilters {
  applications: string[];
  rollupPeriod: string;
  period: string[];
}

export interface BusinessReportingData {
  filters: BusinessReportingFilters;
  filteredTicketData: TicketsParsedDataItem[];
  filteredUserMetricsData: UserMetricsParsedData[];
  processedTicketData: ProcessedBusinessReportingData;
  processedUserData: ProcessedBusinessReportingUserData;
  originalFilterOptions: string;
  isLoading: boolean;
}

interface BusinessReportingSelectionFiltersProps {
  onFiltersChange?: (data: BusinessReportingData) => void;
}

export interface FilterableItem {
  application_name?: string;
  application_type?: string;
  create_date_UserTimeZone?: string;
  log_month?: string;
}

export interface FilterConfig<T> {
  getApplicationField: (item: T) => string;
  getDateField: (item: T) => string;
  dateFormat?: string;
  customFilters?: ((item: T) => boolean)[];
  /** If provided, items matching this predicate bypass the application filter (always included regardless of selected applications) */
  shouldBypassApplicationFilter?: (item: T) => boolean;
}

// Generic filter function
// Check if item passes application filter
const passesApplicationFilter = <T extends {}>(item: T, applications: string[], config: FilterConfig<T>): boolean => {
  if (applications.length === 0) return true;
  // Allow items that match the bypass predicate to pass regardless of application filter
  if (config.shouldBypassApplicationFilter && config.shouldBypassApplicationFilter(item)) return true;
  const applicationValue = config.getApplicationField(item);
  return !!applicationValue && applications.includes(applicationValue);
};

// Check if item passes period filter
const passesPeriodFilter = <T extends {}>(item: T, period: string[], rollupPeriod: string, config: FilterConfig<T>): boolean => {
  if (period.length === 0) return true;

  const dateField = config.getDateField(item);
  if (!dateField) return false;

  const dateFormat = config.dateFormat || FilterDateFormat_MMM_YYYY;
  const dateValue = moment(dateField).format(dateFormat);
  const periodValues = period.map((p) => (typeof p === 'string' ? p : (p as { value?: string })?.value || String(p)));

  if (rollupPeriod === DURATION_OPTIONS.QUARTERLY) {
    const allowedMonths = periodValues.flatMap((quarter) => convertQuarterToMonths(quarter));
    return allowedMonths.includes(dateValue);
  }

  return periodValues.includes(dateValue);
};

// Check if item passes custom filters
const passesCustomFilters = <T extends {}>(item: T, customFilters?: ((item: T) => boolean)[]): boolean => {
  return !customFilters || customFilters.every((filter) => filter(item));
};

const applyBusinessReportingFilters = <T extends FilterableItem>(data: T[], filters: BusinessReportingFilters, config: FilterConfig<T>): T[] => {
  try {
    const dateFormat = config.dateFormat || FilterDateFormat_MMM_YYYY;

    const filteredData = data.filter((item) => {
      return (
        passesApplicationFilter(item, filters.applications, config) &&
        passesPeriodFilter(item, filters.period, filters.rollupPeriod, config) &&
        passesCustomFilters(item, config.customFilters)
      );
    });

    logger.debug('Applied business reporting filters', {
      type: data[0]?.constructor.name,
      originalCount: data.length,
      filteredCount: filteredData.length,
      filters
    });

    return filteredData;
  } catch (error) {
    logger.error('Error applying business reporting filters', error as Error);
    return [];
  }
};

export const BusinessReportingSelectionFilters: React.FC<BusinessReportingSelectionFiltersProps> = ({ onFiltersChange }) => {
  const sharedFilters = useSharedFilters();
  const ticketsCtx = useTickets();
  const umCtx = useUserMetrics();

  // State management
  const [filterState, setFilterState] = useState({
    applications: [] as MultiselectProps.Options,
    rollupPeriod: DURATION_OPTIONS.MONTHLY,
    period: [] as MultiselectProps.Options,
    availableApplications: [] as string[],
    availablePeriods: [] as OptionItem[],
    isInitialized: false
  });

  const [originalFilterOptions, setOriginalFilterOptions] = useState<string>('');

  // Ref to track last processed state to prevent unnecessary updates
  const lastFilterStateRef = useRef<BusinessReportingFilters | null>(null);

  // Memoized values
  const rollupPeriodOptions = useMemo(
    () => [
      { label: 'Quarterly', value: DURATION_OPTIONS.QUARTERLY },
      { label: 'Monthly', value: DURATION_OPTIONS.MONTHLY }
    ],
    []
  );

  const periodOptions = useMemo(
    () =>
      filterState.rollupPeriod === DURATION_OPTIONS.QUARTERLY
        ? parseObjectToMultiSelectOptions_Sorted(
            filterState.availablePeriods.map((p) => p.label),
            'All Quarters'
          )
        : returnFilterAgeSorted(sortedDateArrayMMMYYYYFormat(filterState.availablePeriods.map((p) => p.label)), 'All Months'),
    [filterState.rollupPeriod, filterState.availablePeriods]
  );

  // Validate inputs
  const validateInputs = (selectedPeriod: MultiselectProps.Options, selectedRollupPeriod: string): boolean => {
    if (!validatePeriodSelection(selectedPeriod as OptionItem[], selectedRollupPeriod)) {
      logger.warn('Invalid period selection', { selectedPeriod, selectedRollupPeriod });
      return false;
    }
    return true;
  };

  // Create filters object
  const createFiltersObject = (
    selectedPeriod: MultiselectProps.Options,
    selectedRollupPeriod: string,
    selectedApplications: MultiselectProps.Options
  ): BusinessReportingFilters => ({
    applications: optionsObjectToStringArray(selectedApplications),
    rollupPeriod: selectedRollupPeriod,
    period: optionsObjectToStringArray(selectedPeriod)
  });

  // Set loading state
  const setLoadingState = (filters: BusinessReportingFilters): void => {
    if (onFiltersChange) {
      onFiltersChange({
        filters,
        filteredTicketData: [],
        filteredUserMetricsData: [],
        processedTicketData: processBusinessReportingTicketData([], filters),
        processedUserData: processBusinessReportingUserData([], filters),
        originalFilterOptions,
        isLoading: true
      });
    }
  };

  // Set error state
  const setErrorState = (filters: BusinessReportingFilters): void => {
    if (onFiltersChange) {
      onFiltersChange({
        filters,
        filteredTicketData: [],
        filteredUserMetricsData: [],
        processedTicketData: processBusinessReportingTicketData([], filters),
        processedUserData: processBusinessReportingUserData([], filters),
        originalFilterOptions,
        isLoading: false
      });
    }
  };

  // Set success state
  const setSuccessState = (data: Omit<BusinessReportingData, 'isLoading'>): void => {
    if (onFiltersChange) {
      onFiltersChange({
        ...data,
        isLoading: false
      });
    }
  };

  // Get context data
  const getContextData = (): { ticketsData: TicketsParsedDataItem[]; userMetricsData: UserMetricsParsedData[] } => ({
    ticketsData: ticketsCtx.finTechOpsTicketsData?.data || [],
    userMetricsData: umCtx.userMetricsWithUserData?.data || []
  });

  // Filter and process data
  const filterAndProcessData = async (filters: BusinessReportingFilters) => {
    const { ticketsData, userMetricsData } = getContextData();

    // Filter data
    const filteredTicketData = applyBusinessReportingFilters(ticketsData, filters, {
      getApplicationField: (item) => item.application_name,
      getDateField: (item) => item.create_date_UserTimeZone
    });

    const filteredUserMetricsData = applyBusinessReportingFilters(userMetricsData, filters, {
      getApplicationField: (item) => item.application_type,
      getDateField: (item) => item.log_month,
      // Preserve "All Applications" aggregate records needed by getTotalViewsByApplicationType
      // and getTotalUniqueUsersByApplicationAndMonth charts which filter on application === ALL_APPLICATIONS
      shouldBypassApplicationFilter: (item) => item.application === ALL_APPLICATIONS
    });

    // Process data
    const [processedTicketData, processedUserData] = await Promise.all([
      Promise.resolve(processBusinessReportingTicketData(filteredTicketData, filters)),
      Promise.resolve(processBusinessReportingUserData(filteredUserMetricsData, filters))
    ]);

    return {
      filters,
      filteredTicketData,
      filteredUserMetricsData,
      processedTicketData,
      processedUserData,
      originalFilterOptions
    };
  };

  // Performance logging utility
  const withPerformanceLogging = async <T,>(operation: () => Promise<T>, operationName: string): Promise<T> => {
    const start = getCurrentTime();
    try {
      const result = await operation();
      logger.info(`${operationName} completed in ${getTimeDifference(start)} ms`);
      return result;
    } catch (error) {
      logger.error(`${operationName} failed after ${getTimeDifference(start)} ms`, error as Error);
      throw error;
    }
  };

  // Main orchestrator function
  const updateBusinessReportingData = async (
    selectedPeriod: MultiselectProps.Options,
    selectedRollupPeriod: string,
    selectedApplications: MultiselectProps.Options
  ) => {
    try {
      const filters = createFiltersObject(selectedPeriod, selectedRollupPeriod, selectedApplications);

      if (!validateInputs(selectedPeriod, selectedRollupPeriod)) {
        setErrorState(filters);
        return;
      }

      setLoadingState(filters);

      const processedData = await withPerformanceLogging(() => filterAndProcessData(filters), 'Business reporting data processing');

      setSuccessState(processedData);
    } catch (error) {
      logger.error('Failed to update business reporting data', error as Error);
      const filters = createFiltersObject(selectedPeriod, selectedRollupPeriod, selectedApplications);
      setErrorState(filters);
    }
  };

  const initializeFiltersAndProcessData = async () => {
    try {
      const ticketsData = ticketsCtx.finTechOpsTicketsData?.data || [];
      const userMetricsData = umCtx.userMetricsWithUserData?.data || [];

      const businessApplications = getBusinessReportingApplications(ticketsData, userMetricsData);
      const defaultSelections = getDefaultSelections(businessApplications, DURATION_OPTIONS.MONTHLY);
      const periods = defaultSelections.rollupPeriod === DURATION_OPTIONS.QUARTERLY ? getQuarterlyOptions() : getMonthlyOptions();

      // Capture original filter options if not already set
      if (!originalFilterOptions) {
        const initialFilters = {
          applications: optionsObjectToStringArray(defaultSelections.applications),
          rollupPeriod: defaultSelections.rollupPeriod,
          period: optionsObjectToStringArray(defaultSelections.period)
        };
        setOriginalFilterOptions(JSON.stringify(initialFilters));
      }

      setFilterState((prev) => ({
        ...prev,
        applications: defaultSelections.applications,
        rollupPeriod: defaultSelections.rollupPeriod as DURATION_OPTIONS,
        availableApplications: businessApplications,
        availablePeriods: periods,
        period: defaultSelections.period, // Use all periods from defaultSelections
        isInitialized: true
      }));

      if (defaultSelections.period.length > 0) {
        // Initialize ref to prevent unnecessary updates on first dropdown interaction
        lastFilterStateRef.current = {
          applications: optionsObjectToStringArray(defaultSelections.applications),
          rollupPeriod: defaultSelections.rollupPeriod,
          period: optionsObjectToStringArray(defaultSelections.period)
        };

        await updateBusinessReportingData(
          defaultSelections.period, // Use all periods from defaultSelections
          defaultSelections.rollupPeriod,
          defaultSelections.applications
        );
      }
    } catch (error) {
      logger.error('Failed to initialize filters', error as Error);
    }
  };

  // Event handlers
  const handleFilterChange = (type: 'applications' | 'period' | 'rollupPeriod', value: MultiselectProps.Options | SelectProps.Option) => {
    setFilterState((prev) => {
      const newState = { ...prev };

      if (type === 'rollupPeriod' && 'value' in value) {
        newState.rollupPeriod = value.value as DURATION_OPTIONS;
        newState.availablePeriods = value.value === DURATION_OPTIONS.QUARTERLY ? getQuarterlyOptions() : getMonthlyOptions();
        // Select all periods when rollup period changes
        newState.period = newState.availablePeriods;
      } else if (type === 'applications' || type === 'period') {
        newState[type] = value as MultiselectProps.Options;
      }

      return newState;
    });
  };

  const onFilterBlur = () => {
    const currentFilters: BusinessReportingFilters = {
      applications: optionsObjectToStringArray(filterState.applications),
      rollupPeriod: filterState.rollupPeriod,
      period: optionsObjectToStringArray(filterState.period)
    };

    if (!areFiltersEqual(currentFilters, lastFilterStateRef.current)) {
      updateBusinessReportingData(filterState.period, filterState.rollupPeriod, filterState.applications);
      lastFilterStateRef.current = currentFilters;
    }
  };

  const resetFilters = () => {
    setFilterState((prev) => ({
      ...prev,
      isInitialized: false
    }));
  };

  // Initialize filters and data
  useEffect(() => {
    if (ticketsCtx.finTechOpsTicketsData?.data && umCtx.userMetricsWithUserData?.data && !filterState.isInitialized) {
      initializeFiltersAndProcessData();
    }
  }, [ticketsCtx.finTechOpsTicketsData, umCtx.userMetricsWithUserData, filterState.isInitialized]);

  // Handle rollup period changes
  useEffect(() => {
    if (filterState.isInitialized && filterState.period.length > 0) {
      updateBusinessReportingData(filterState.period, filterState.rollupPeriod, filterState.applications);
    }
  }, [filterState.rollupPeriod, filterState.isInitialized]);

  // Render
  return (
    <div>
      <SpaceBetween size="l">
        <Box>
          <Container
            header={
              <SpaceBetween size="m" direction="horizontal" alignItems="center">
                <Header variant="h2">{Titles.TITLE_BUSINESS_REPORTING}</Header>
                <Button onClick={resetFilters}>Clear Filters</Button>
              </SpaceBetween>
            }
          >
            <Box>
              <div className={styles.multiselectContainer}>
                <div className={styles.multiselect}>
                  <FormField label={filtersLabel('Applications')}>
                    <Multiselect
                      placeholder={getMultiSelectPlaceHolderValue(filterState.applications, 'Applications', filterState.availableApplications)}
                      options={parseObjectToMultiSelectOptions_Sorted(filterState.availableApplications, 'All Applications')}
                      selectedOptions={filterState.applications}
                      onChange={({ detail }) => handleFilterChange('applications', detail.selectedOptions)}
                      onBlur={onFilterBlur}
                      deselectAriaLabel={(e) => `Remove ${e.label}`}
                      selectedAriaLabel="Selected"
                      expandToViewport
                      hideTokens
                      virtualScroll
                      filteringType="auto"
                      loadingText="Loading Applications"
                      errorText="Unable to load applications"
                      statusType={sharedFilters.dashboardFilterStatus}
                      disabled={!filterState.isInitialized}
                    />
                  </FormField>
                </div>
                <div className={styles.multiselect}>
                  <FormField label={filtersLabel('Rollup Period')}>
                    <Select
                      selectedOption={rollupPeriodOptions.find((opt) => opt.value === filterState.rollupPeriod) || null}
                      onChange={({ detail }) => handleFilterChange('rollupPeriod', detail.selectedOption)}
                      options={rollupPeriodOptions}
                      loadingText="Loading Rollup Periods"
                      errorText="Unable to load rollup periods"
                      statusType={sharedFilters.dashboardFilterStatus}
                      disabled={!filterState.isInitialized}
                      expandToViewport
                    />
                  </FormField>
                </div>
                <div className={styles.multiselect}>
                  <FormField label={filtersLabel('Period')}>
                    <Multiselect
                      placeholder={getMultiSelectPlaceHolderValue(
                        filterState.period,
                        filterState.rollupPeriod === DURATION_OPTIONS.QUARTERLY ? 'Quarters' : 'Months',
                        filterState.availablePeriods.map((p) => p.label)
                      )}
                      options={periodOptions}
                      selectedOptions={filterState.period}
                      onChange={({ detail }) => handleFilterChange('period', detail.selectedOptions)}
                      onBlur={onFilterBlur}
                      deselectAriaLabel={(e) => `Remove ${e.label}`}
                      selectedAriaLabel="Selected"
                      expandToViewport
                      hideTokens
                      virtualScroll
                      filteringType="auto"
                      loadingText={`Loading ${filterState.rollupPeriod === DURATION_OPTIONS.QUARTERLY ? 'Quarters' : 'Months'}`}
                      errorText={`Unable to load ${filterState.rollupPeriod === DURATION_OPTIONS.QUARTERLY ? 'quarters' : 'months'}`}
                      statusType={sharedFilters.dashboardFilterStatus}
                      disabled={!filterState.isInitialized || filterState.availablePeriods.length === 0}
                    />
                  </FormField>
                </div>
              </div>
            </Box>
          </Container>
        </Box>
      </SpaceBetween>
    </div>
  );
};
